// useProduction — the Assets → Production data hooks: the whole-pack editor read/PUT, the mic
// catalog, and the venue-advance token mint/rotate. The pack GET is production:read (band-shared) but the PUT + the
// venue-token routes are production:write (the crew-ownership gate — the screen cues with useCapability). Every band
// read forces the tenant key ['band', bandId, …] via useBandQuery; the write invalidates it on success. The mic
// catalog is a GLOBAL (non-tenant) reference read — a plain useQuery keyed ['mics'].
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type ProductionRead = components["schemas"]["ProductionRead"];
export type ProductionFileView = components["schemas"]["ProductionFileView"];
export type InputChannelView = components["schemas"]["InputChannelView"];
export type ProductionNotesView = components["schemas"]["ProductionNotesView"];
export type RiderEssential = components["schemas"]["RiderEssential"];
export type UpdateProductionRequest = components["schemas"]["UpdateProductionRequest"];
export type ProductionFileInput = components["schemas"]["ProductionFileInput"];
export type InputChannelInput = components["schemas"]["InputChannelInput"];
export type VenueTokenView = components["schemas"]["VenueTokenView"];
export type MicView = components["schemas"]["MicView"];
export type AdvanceLookView = components["schemas"]["AdvanceLookView"];
export type AdvanceLookListResponse = components["schemas"]["AdvanceLookListResponse"];
export type AdvanceDepartmentView = components["schemas"]["AdvanceDepartmentView"];
export type AdvanceDepartmentListResponse = components["schemas"]["AdvanceDepartmentListResponse"];
export type AdvanceDepartmentFileInput = components["schemas"]["AdvanceDepartmentFileInput"];
export type UpdateAdvanceLookRequest = components["schemas"]["UpdateAdvanceLookRequest"];
export type UpdateVenueLinkRequest = components["schemas"]["UpdateVenueLinkRequest"];

/**
 * WHICH ADVANCE a hook is reading. `null` is `Regular` — the band's own advance, which is the ABSENCE of a look
 * rather than a look named "Regular", exactly as it is in the schema.
 *
 * It is part of every production query KEY, and that is load-bearing rather than tidy: without it, switching looks
 * paints the previous advance's pack out of cache under the new look's name, and the editor would then SAVE that
 * draft over the look you switched to. `'regular'` stands in for null in the key because a key segment of
 * `undefined` is dropped by react-query and would collapse Regular's key onto the un-scoped one.
 */
export type LookId = string | null;
const lookKey = (lookId: LookId) => lookId ?? "regular";

// The editor read carries LOCAL DRAFT state in the screen, so a BACKGROUND refetch must never clobber unsaved edits
// (the /S5/S6 draft-clobber fix): staleTime:Infinity + no focus/reconnect refetch.
const EDITOR_READ = { staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false } as const;

export function useProduction(lookId: LookId = null) {
  const { api } = useApp();
  return useBandQuery<ProductionRead>(
    ["production", lookKey(lookId)],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/production", {
        params: { path: { bandId }, query: lookId ? { look: lookId } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    // `keepPreviousData` holds the OUTGOING advance's pack on screen while the next one loads, and that is
    // structural rather than cosmetic: without it `data` goes undefined on every switch, the screen falls to its
    // loading branch, and the editor — which is where the look SWITCHER lives — unmounts. The switcher would then
    // disappear at the exact moment it is used. The pack's own `lookId` is what tells the editor the new advance
    // has actually arrived; until then it is knowingly showing the previous one.
    { ...EDITOR_READ, placeholderData: keepPreviousData },
  );
}

export function useUpdateProduction(lookId: LookId = null) {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<ProductionRead, UpdateProductionRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/production", {
        params: { path: { bandId: band }, query: lookId ? { look: lookId } : {} },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    // Invalidates THIS look only. A band-wide invalidation would refetch every other advance's pack on every
    // save — and, worse, the screen holds a draft per look, so it would put a background refetch in front of
    // unsaved edits the user has not returned to yet.
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "production", lookKey(lookId)] }) },
  );
}

// The venue-advance token (production:write). GET is its own read (the token VALUE is a bearer credential kept off
// the broad production:read pack GET); POST mints (first time) or rotates (overwrites → the old link 404s).
export function useVenueToken(lookId: LookId = null) {
  const { api } = useApp();
  return useBandQuery<VenueTokenView>(
    ["venue-token", lookKey(lookId)],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/production/venue-token", {
        params: { path: { bandId }, query: lookId ? { look: lookId } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    EDITOR_READ,
  );
}

export function useRotateVenueToken(lookId: LookId = null) {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<VenueTokenView, void>(
    async (band) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/production/venue-token", {
        params: { path: { bandId: band }, query: lookId ? { look: lookId } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "venue-token", lookKey(lookId)] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "production", lookKey(lookId)] });   // HasVenueToken flips on first mint
        void qc.invalidateQueries({ queryKey: ["band", bandId, "advance-looks"] });                 // the pill's minted state
      },
    },
  );
}

// ---- advance looks --------------------------------------------------------------------------------------------
// A look is a variant of the pack carrying its OWN venue link, so a festival never sees the club advance.
// `Regular` is NOT in this list — it is the absence of a look, so the screen draws it as a fixed leading pill.

export function useAdvanceLooks() {
  const { api } = useApp();
  return useBandQuery<AdvanceLookListResponse>(
    ["advance-looks"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/production/looks", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
  );
}

/**
 * Create an advance. On STANDARD the caller passes a name alone and gets what it has always got — a copy of
 * Regular — because the server resolves an absent seed to `legacy`. The PRO catalogue additionally passes the
 * kind, the year and one of the three seeds.
 */
export function useCreateAdvanceLook() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<AdvanceLookView, {
    name: string; kind?: string; year?: number; seed?: string; sourceLookId?: string;
  }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/production/looks", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "advance-looks"] }) },
  );
}

/**
 * Update an advance's IDENTITY — name, kind, year. PATCH: whatever you leave off is unchanged, which is what
 * lets the Standard pill row send `{ name }` alone without stripping a Pro band's kind and year.
 */
export function useUpdateAdvanceLook() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<AdvanceLookView, { lookId: string } & UpdateAdvanceLookRequest>(
    async (band, { lookId, ...body }) => {
      const { data, error, response } = await api.PATCH("/api/v1/bands/{bandId}/production/looks/{lookId}", {
        params: { path: { bandId: band, lookId } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "advance-looks"] }) },
  );
}

// ---- PRO: the twelve department tabs (ART-146) -----------------------------------------------------------------

/**
 * One advance's twelve departments — files and notes. ALL twelve always come back, empty ones included: the tab
 * strip is a fixed vocabulary and the client must never have to invent the departments the server did not name.
 *
 * Keyed on the look for the same reason the pack read is: without it, switching advance paints the previous one's
 * departments under the new one's name, and the tab editor would then save that draft over it.
 */
export function useAdvanceDepartments(lookId: LookId = null) {
  const { api } = useApp();
  return useBandQuery<AdvanceDepartmentListResponse>(
    ["advance-departments", lookKey(lookId)],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/production/departments", {
        params: { path: { bandId }, query: lookId ? { look: lookId } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { ...EDITOR_READ, placeholderData: keepPreviousData },
  );
}

/**
 * Save ONE department. The narrow scope is the server's contract and the client must respect it in kind: send the
 * department you are editing and nothing else — the other eleven are untouched precisely because they are absent.
 */
export function useUpdateAdvanceDepartment(lookId: LookId = null) {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<AdvanceDepartmentView, {
    department: string; notes?: string | null; files?: AdvanceDepartmentFileInput[] | null;
  }>(
    async (band, { department, notes, files }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/production/departments/{department}", {
        params: { path: { bandId: band, department }, query: lookId ? { look: lookId } : {} },
        body: { notes, files },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "advance-departments", lookKey(lookId)] }) },
  );
}

/**
 * Set / replace / remove this advance link's password and expiry — and MINT the link if it has none ("set anything
 * below to mint one"). PATCH: absent is unchanged, and each removal is its own explicit flag, because "leave the
 * password alone" and "remove the password" are different requests a nullable field cannot tell apart.
 *
 * A 402 here is the tier gate, and it fires only on a SET: a band that has lapsed from Pro can always still clear.
 */
export function useUpdateVenueLink(lookId: LookId = null) {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<VenueTokenView, UpdateVenueLinkRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PATCH("/api/v1/bands/{bandId}/production/venue-token/link", {
        params: { path: { bandId: band }, query: lookId ? { look: lookId } : {} },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "venue-token", lookKey(lookId)] });
        // The catalogue's link-state chips are on the LOOKS read, not this one — and this call can MINT, so the
        // list's `hasVenueToken` moves too.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "advance-looks"] });
      },
    },
  );
}

export function useDeleteAdvanceLook() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<void, { lookId: string }>(
    async (band, { lookId }) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/production/looks/{lookId}", {
        params: { path: { bandId: band, lookId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_r, { lookId }) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "advance-looks"] });
        // Drop the deleted look's cached pack and token outright rather than marking them stale: the rows are gone,
        // so a refetch would only 404. Anything still holding that id must fall back to Regular.
        qc.removeQueries({ queryKey: ["band", bandId, "production", lookId] });
        qc.removeQueries({ queryKey: ["band", bandId, "venue-token", lookId] });
        qc.removeQueries({ queryKey: ["band", bandId, "advance-departments", lookId] });
      },
    },
  );
}

// The GLOBAL mic catalog (band:read, non-tenant) — a stable app-wide reference list; the band's custom mics are
// surfaced by the screen from the loaded pack's channels. Long-lived (the catalog rarely changes).
export function useMics() {
  const { api } = useApp();
  return useQuery<MicView[]>({
    queryKey: ["mics"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/mics", {});
      if (!response.ok || !data?.items) throw new ApiError(response.status, error);
      return data.items;
    },
    staleTime: 1000 * 60 * 60,
  });
}

// The BAND's persistent custom-mic list (ART-166) — free-typed gear banked so it autocompletes next time. Unlike
// the global catalog above this is band-scoped (production:write), so it rides useBandQuery's ['band', id, …] key.
export function useBandCustomMics() {
  const { api } = useApp();
  return useBandQuery<string[]>(
    ["mics", "custom"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/mics/custom", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.items) throw new ApiError(response.status, error);
      return data.items;
    },
    { staleTime: 1000 * 60 * 5 },
  );
}

// Bank one free-typed mic. The POST is idempotent (a catalog/own-list/too-short label is a server no-op) and
// returns the fresh list, so onSuccess writes it straight into the customs cache — no refetch round-trip.
export function useBankCustomMic() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<string[], string>(
    async (band, label) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/mics/custom", {
        params: { path: { bandId: band } },
        body: { label },
      });
      if (!response.ok || !data?.items) throw new ApiError(response.status, error);
      return data.items;
    },
    { onSuccess: (items) => qc.setQueryData(["band", bandId, "mics", "custom"], items) },
  );
}
